import { cloneElement, isValidElement, useRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from '../lib/cx';
import { useClickOutside, useControllableState, useOnEscape } from '../lib/hooks';

export interface MenuProps {
  /** The clickable trigger (a button). It receives `aria-haspopup`/`aria-expanded`
   *  itself: on the wrapping span they were invalid ARIA and never announced. */
  trigger: ReactNode;
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: 'start' | 'end';
  /** Surface width in px (default: fits content, min 220). */
  width?: number;
  /** Accessible name for the menu surface. */
  'aria-label': string;
  className?: string;
}

/** Dropdown menu surface — the top-bar user menu and the business switcher
 *  (brief §7.3). Closes on Escape, click-outside, and after an item runs. */
export function Menu({ trigger, children, open, defaultOpen = false, onOpenChange, align = 'start', width, className, ...rest }: MenuProps) {
  const [isOpen, setOpen] = useControllableState<boolean>(open, defaultOpen, onOpenChange);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), isOpen);
  useOnEscape(() => setOpen(false), isOpen);

  return (
    <div className="tc-menu-anchor" ref={ref}>
      <span className="tc-menu-anchor__trigger" onClick={() => setOpen(!isOpen)}>
        {isValidElement<Record<string, unknown>>(trigger)
          ? cloneElement(trigger, { 'aria-haspopup': 'menu', 'aria-expanded': isOpen })
          : trigger}
      </span>
      {isOpen && (
        <div
          role="menu"
          aria-label={rest['aria-label']}
          style={width ? { width } : undefined}
          className={cx('tc-menu', align === 'end' && 'tc-menu--end', className)}
          // Any item click closes the menu; items that shouldn't close stop propagation.
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export interface MenuItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: ReactNode;
  /** Trailing content: a shortcut, count, phone number, status pill… */
  meta?: ReactNode;
  /** Marks the item as the current selection (radio semantics). */
  selected?: boolean;
  tone?: 'default' | 'primary' | 'danger';
}

/** One actionable row inside a {@link Menu}. */
export function MenuItem({ icon, meta, selected, tone = 'default', className, children, type = 'button', ...rest }: MenuItemProps) {
  return (
    <button
      type={type}
      role={selected === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={selected}
      className={cx('tc-menu__item', tone !== 'default' && `tc-menu__item--${tone}`, selected && 'tc-menu__item--selected', className)}
      {...rest}
    >
      {icon && <span className="tc-menu__item-icon">{icon}</span>}
      <span className="tc-menu__item-label">{children}</span>
      {meta && <span className="tc-menu__item-meta">{meta}</span>}
    </button>
  );
}

/** A non-interactive row (headers, the signed-in identity block, hints). */
export function MenuSection({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('tc-menu__section', className)}>{children}</div>;
}

export function MenuSeparator() {
  return <div className="tc-menu__sep" role="separator" />;
}
